import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { coursesAPI } from '../config/api'
import { getApiBaseUrlWithoutApi } from '../config/api'
import 'primeicons/primeicons.css'

export default function RenewalWarningCard() {
  const { theme } = useTheme()
  const { user } = useAuth()
  const [showCard, setShowCard] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null)

  useEffect(() => {
    if (!user || !user.subscription_expiry_date) {
      setShowCard(false)
      return
    }

    // Tính số ngày còn lại
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiryDate = new Date(user.subscription_expiry_date)
    expiryDate.setHours(0, 0, 0, 0)
    
    const diffTime = expiryDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    setDaysRemaining(diffDays)
    
    // Chỉ hiển thị nếu còn dưới 3 ngày (1, 2 ngày) và chưa hết hạn
    if (diffDays > 0 && diffDays < 3) {
      setShowCard(true)
    } else {
      setShowCard(false)
    }
  }, [user])

  useEffect(() => {
    // Fetch QR code khi cần hiển thị modal
    const fetchQRCode = async () => {
      try {
        const coursesData = await coursesAPI.getPublicCourses({ type: 'online' })
        const coursesList = Array.isArray(coursesData)
          ? coursesData
          : Array.isArray(coursesData?.results)
            ? coursesData.results
            : Array.isArray(coursesData?.data)
              ? coursesData.data
              : []
        
        // Tìm khóa học có QR code (ưu tiên khóa học đầu tiên)
        const courseWithQR = coursesList.find((c: any) => c.payment_qr_code) || coursesList[0]
        if (courseWithQR) {
          const API_BASE_URL = getApiBaseUrlWithoutApi()
          let qrCode = courseWithQR.payment_qr_code || courseWithQR.qr_code || courseWithQR.qr_image || null
          if (qrCode && qrCode.startsWith('/media/')) {
            qrCode = `${API_BASE_URL}${qrCode}`
          } else if (qrCode && !qrCode.startsWith('http') && !qrCode.startsWith('/images/')) {
            qrCode = `${API_BASE_URL}${qrCode}`
          }
          setQrCodeUrl(qrCode)
        }
      } catch (error) {
        // Error fetching QR code
      }
    }

    if (showQRModal) {
      fetchQRCode()
    }
  }, [showQRModal])

  const handleGetQR = () => {
    setShowQRModal(true)
  }

  const handleCloseCard = () => {
    setShowCard(false)
  }

  const handleCloseQRModal = () => {
    setShowQRModal(false)
  }

  if (!showCard || !daysRemaining) {
    return null
  }

  return (
    <>
      {/* Warning Card */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleCloseCard}
        />
        
        {/* Card */}
        <div className={`relative rounded-2xl border-2 p-4 sm:p-6 md:p-8 shadow-2xl w-full max-w-sm sm:max-w-md md:max-w-lg ${
          theme === 'dark'
            ? 'bg-black/95 border-yellow-500/50'
            : 'bg-white border-yellow-400/50'
        }`}>
          {/* Close Button */}
          <button
            onClick={handleCloseCard}
            className={`absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition ${
              theme === 'dark'
                ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
                : 'bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-600'
            }`}
          >
            <i className="pi pi-times text-sm sm:text-base md:text-lg"></i>
          </button>

          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center ${
              theme === 'dark' ? 'bg-yellow-500/20' : 'bg-yellow-400/20'
            }`}>
              <i className={`pi pi-exclamation-triangle text-xl sm:text-2xl md:text-3xl ${
                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`}></i>
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className={`text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Bạn sắp hết hạn khóa học
              </h3>
              <p className={`text-sm sm:text-base md:text-lg mb-3 sm:mb-4 leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Còn <span className={`font-bold ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>{daysRemaining}</span> ngày nữa là hết hạn. Vui lòng thanh toán để gia hạn.
              </p>
              
              <button
                onClick={handleGetQR}
                className={`w-full py-2.5 sm:py-3 md:py-4 rounded-xl text-sm sm:text-base md:text-lg font-bold transition shadow-lg hover:shadow-xl ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white'
                    : 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900'
                }`}
              >
                Nhận ngay
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={handleCloseQRModal}
          />
          
          {/* Modal */}
          <div className={`relative z-10 w-full max-w-md rounded-2xl border-2 ${
            theme === 'dark' 
              ? 'bg-black/95 border-yellow-500/50' 
              : 'bg-white border-yellow-400/50'
          } shadow-2xl`}>
            {/* Close Button */}
            <button
              onClick={handleCloseQRModal}
              className={`absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition ${
                theme === 'dark'
                  ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
                  : 'bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-600'
              }`}
            >
              <i className="pi pi-times text-lg"></i>
            </button>

            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className={`text-xl md:text-2xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Mã QR Thanh Toán
                </h2>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Quét QR để chuyển khoản nhanh
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className={`p-4 rounded-xl ${
                  theme === 'dark' ? 'bg-white' : 'bg-gray-100'
                }`}>
                  {qrCodeUrl ? (
                    <img
                      src="/images/qr-code.png"
                      alt="QR chuyển khoản"
                      className="w-64 h-64 object-contain"
                    />
                  ) : (
                    <img
                      src="/images/qr-code.png"
                      alt="QR chuyển khoản"
                      className="w-64 h-64 object-contain"
                    />
                  )}
                </div>
              </div>

              {/* Bank Info */}
              <div className={`border-2 rounded-xl p-4 mb-4 ${
                theme === 'dark' 
                  ? 'bg-yellow-500/10 border-yellow-500/30' 
                  : 'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Ngân hàng:
                    </span>
                    <span className={`font-semibold ${
                      theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                    }`}>
                      ACB
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Số tài khoản:
                    </span>
                    <span className={`font-semibold ${
                      theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                    }`}>
                      17386611
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Chủ tài khoản:
                    </span>
                    <span className={`font-semibold ${
                      theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                    }`}>
                      LE DUY KHANG
                    </span>
                  </div>
                </div>
              </div>

              {/* Note */}
              <p className={`text-xs text-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Sau khi chuyển khoản, vui lòng gửi ảnh biên lai qua Zalo{' '}
                <span className={`font-semibold ${
                  theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                }`}>
                  0879933020
                </span>{' '}
                để kích hoạt khóa học.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

